import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Network, Plus, Edit2, Trash2 } from 'lucide-react';
import { DataTable } from '../../../../components/ui/DataTable';
import { PageHeader } from '../../../../components/ui/PageHeader';
import { FilterBar } from '../../../../components/ui/FilterBar';
import { SearchInput } from '../../../../components/ui/SearchInput';
import { Button } from '../../../../components/ui/Button';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { selectIsRunning } from '../../../../features/feedback/feedbackSlice';
import {
  fetchStaffExternalIntegrations,
  fetchSellersByAdminThunk,
  deleteExternalIntegrationThunk,
  createExternalIntegrationThunk,
  updateExternalIntegrationThunk
} from '../../../../features/externalIntegrations/externalIntegrationsThunks';
import { fetchAdminOptions } from '../../../../features/admins/adminsThunks';
import {
  setPage,
  setRowsPerPage,
  setSearchFilter
} from '../../../../features/externalIntegrations/externalIntegrationsSlice';
import CreateIntegrationModal from './CreateIntegrationModal';
import EditIntegrationModal from './EditIntegrationModal';

export const ExternalIntegrations = () => {
  const dispatch = useDispatch();
  const {
    integrations,
    sellers,
    loading,
    error,
    filters,
    totalCount
  } = useSelector(state => state.externalIntegrations);
  // Each dialog waits on its own save; `loading` is also the list reloading.
  const creating = useSelector(selectIsRunning(createExternalIntegrationThunk));
  const updating = useSelector(selectIsRunning(updateExternalIntegrationThunk));

  const [localSearch, setLocalSearch] = useState(filters.search || '');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);

  // Debounce for search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (localSearch !== filters.search) {
        dispatch(setSearchFilter(localSearch));
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [localSearch, filters.search, dispatch]);

  useEffect(() => {
    dispatch(fetchSellersByAdminThunk());
  }, [dispatch]);

  // Fetch when relevant filters change
  useEffect(() => {
    dispatch(fetchStaffExternalIntegrations());
    // The operators, for the owner of each row and for the create form.
    dispatch(fetchAdminOptions());
  }, [
    dispatch,
    filters.search,
    filters.page,
    filters.rowsPerPage
  ]);

  const handleDelete = (id) => {
    if (window.confirm("¿Estás seguro que quieres eliminar esta integración?")) {
      dispatch(deleteExternalIntegrationThunk({ id, isStaff: true }));
    }
  };

  const handleCreateConfirm = async (payload) => {
    try {
      const { admin_id: adminId, ...integrationData } = payload;
      await dispatch(
        createExternalIntegrationThunk({ integrationData, adminId, isStaff: true })
      ).unwrap();
      setIsCreateModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditConfirm = async (payload) => {
    if (!selectedIntegration) return;
    try {
      await dispatch(updateExternalIntegrationThunk({ id: selectedIntegration.id, data: payload, isStaff: true })).unwrap();
      setIsEditModalOpen(false);
      setSelectedIntegration(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditClick = (integration) => {
    setSelectedIntegration(integration);
    setIsEditModalOpen(true);
  };

  const columns = [
    {
      accessor: 'integration_name',
      header: 'Integración',
      cell: (item) => (
        <div className="flex flex-col items-start gap-1">
          <span className="font-bold text-ninja-dark dark:text-white">
            {item.integration_name}
          </span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300">
            {item.integration_type || 'N/A'}
          </span>
        </div>
      )
    },
    {
      accessor: 'url',
      header: 'URL Destino',
      cell: (item) => (
        <span className="text-text-secondary truncate max-w-[200px] inline-block" title={item.url}>
          {item.url || '-'}
        </span>
      )
    },
    {
      accessor: 'admin',
      header: 'Operador',
      cell: (item) => (
        <div className="flex flex-col">
          <span className="text-ninja-dark dark:text-white">{item.admin?.name || '—'}</span>
          <span className="text-xs text-text-muted mt-0.5">{item.admin?.email || ''}</span>
        </div>
      )
    },
    {
      accessor: 'updated_at',
      header: 'Última Actualización',
      cell: (item) => {
        const dateStr = item.updated_at || item.created_at;
        if (!dateStr) return '-';
        return <span className="text-text-secondary">{new Date(dateStr).toLocaleDateString()}</span>;
      }
    },
    {
      accessor: 'actions',
      header: 'Acciones',
      cell: (item) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="subtle"
            size="sm"
            iconOnly
            icon={Edit2}
            title="Editar Integración"
            aria-label="Editar Integración"
            onClick={() => handleEditClick(item)}
          />
          <PendingButton
            variant="subtle"
            size="sm"
            iconOnly
            icon={Trash2}
            title="Eliminar Integración"
            aria-label="Eliminar Integración"
            className="hover:text-danger"
            pendingOn={deleteExternalIntegrationThunk}
            pendingKey={item.id}
            onClick={() => handleDelete(item.id)}
          />
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <PageHeader
        icon={Network}
        iconClassName="bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400"
        title="Servicios e Integraciones Externas"
        description="Los webhooks y servicios externos configurados por todos los operadores."
        actions={
          <Button variant="primary" icon={Plus} onClick={() => setIsCreateModalOpen(true)}>
            Nueva Integración
          </Button>
        }
      />

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 text-danger p-4 rounded-xl border border-red-200 dark:border-red-900 flex items-center gap-3">
          <span className="font-semibold">Error:</span> {error}
        </div>
      )}

      <FilterBar variant="bare">
        <SearchInput
          variant="bare"
          value={localSearch}
          onChange={setLocalSearch}
          placeholder="Buscar integraciones..."
        />
      </FilterBar>

      <DataTable
        columns={columns}
        data={integrations}
        isLoading={loading}
        totalItems={totalCount}
        currentPage={filters.page}
        itemsPerPage={filters.rowsPerPage}
        itemLabel="integraciones"
        onPageChange={(page) => dispatch(setPage(page))}
        onRowsPerPageChange={(rows) => dispatch(setRowsPerPage(rows))}
        emptyMessage="No se encontraron integraciones configuradas."
      />

      <CreateIntegrationModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onConfirm={handleCreateConfirm}
        loading={creating}
        sellers={sellers}
      />

      <EditIntegrationModal
        open={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedIntegration(null);
        }}
        onConfirm={handleEditConfirm}
        loading={updating}
        initialData={selectedIntegration}
        sellers={sellers}
      />
    </div>
  );
};
